import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Diálogo para que un jefe (admin / admin-grafton / super-admin) asigne qué
 * usuarios pueden editar una vacante ATS y gestionar su proceso.
 *
 * Props:
 * - open: boolean
 * - position: objeto de la vacante (uid, name, usuariosAsignados)
 * - onClose: () => void
 * - onSaved: (positionUid, usuariosAsignados) => void  (para refrescar la lista)
 */
const AtsAssignUsersDialog = ({ open, onClose, position, onSaved }) => {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !position) return;

    const loadUsers = async () => {
      const { authToken, organization, showAlert } = useStore.getState();

      try {
        setLoading(true);
        const response = await api.get(
          `/organizations/users/${organization}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.response?.status_response === 'OK') {
          // Solo se pueden asignar usuarios con rol "user" (los admins ya pueden editar todo)
          const orgUsers = (response.data.response.data || []).filter(
            (u) => u.rol === 'user'
          );
          setUsers(orgUsers);

          const assigned = Array.isArray(position.usuariosAsignados)
            ? position.usuariosAsignados
            : [];
          setSelected(orgUsers.filter((u) => assigned.includes(u.uid)));
        } else {
          showAlert(t('ats.positions.assignDialog.loadError'), 'warning');
        }
      } catch (err) {
        console.error(err);
        showAlert(t('ats.positions.assignDialog.loadError'), 'warning');
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [open, position, t]);

  const handleSave = async () => {
    const { authToken, showAlert } = useStore.getState();
    const usuariosAsignados = selected.map((u) => u.uid);

    try {
      setSaving(true);
      const response = await api.patch(
        `/positions/${position.uid}/assigned-users`,
        { usuariosAsignados },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.response?.status_response === 'OK') {
        showAlert(t('ats.positions.assignDialog.saved'), 'success');
        if (onSaved) onSaved(position.uid, usuariosAsignados);
        onClose();
      } else {
        showAlert(t('ats.positions.assignDialog.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.positions.assignDialog.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('ats.positions.assignDialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('ats.positions.assignDialog.description')}
        </DialogContentText>

        {position?.name && (
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {position.name}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Autocomplete
            multiple
            options={users}
            value={selected}
            onChange={(e, newValue) => setSelected(newValue)}
            getOptionLabel={(option) => option.name || option.email || option.uid}
            isOptionEqualToValue={(option, value) => option.uid === value.uid}
            noOptionsText={t('ats.positions.assignDialog.noOptions')}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name || option.email}
                  {...getTagProps({ index })}
                  key={option.uid}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('ats.positions.assignDialog.selectLabel')}
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('ats.positions.assignDialog.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t('ats.positions.assignDialog.save')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsAssignUsersDialog;
